import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import { MarketLevelMarketTemplateId } from '@ps/web-sports-api-types';
import type {
  EventLevelMarketTemplateId,
  MarketType,
  SingleMarketTemplate
} from '@ps/web-sports-api-types';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { ExpandablePanel, MarketTemplate } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';

import { isTranslatableMarketType } from '../../utils/marketTypes';
import CombinedList from '../CombinedList/CombinedList';

import {
  TXTWCL_FridayXs_Featured_Odds_Boost,
  TXTWCL_FridayXs_Featured_Request_A_Be_ELL,
  TXTWCL_MondayXs_Featured_Odds_Boost,
  TXTWCL_MondayXs_Featured_Request_A_Be_ELL,
  TXTWCL_SaturdayXs_Featured_Odds_Boost,
  TXTWCL_SaturdayXs_Featured_Request_A__ELL,
  TXTWCL_SundayXs_Featured_Odds_Boost,
  TXTWCL_SundayXs_Featured_Request_A_Be_ELL,
  TXTWCL_ThursdayXs_Featured_Odds_Boost,
  TXTWCL_ThursdayXs_Featured_Request_A__ELL,
  TXTWCL_TuesdayXs_Featured_Odds_Boost,
  TXTWCL_TuesdayXs_Featured_Request_A_B_ELL,
  TXTWCL_WednesdayXs_Featured_Odds_Boos_ELL,
  TXTWCL_WednesdayXs_Featured_Request_A_ELL
} from './TemplateGroup.messages.json';
import styles from './TemplateGroup.scss';
import Title from './components/Title/Title';

interface Props {
  isGroupedByDate: boolean;
  templates: SingleMarketTemplate[];
  isOpen: boolean;
}

type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const oddsBoostTranslationMap: Record<Day, TranslationTag> = {
  0: TXTWCL_SundayXs_Featured_Odds_Boost,
  1: TXTWCL_MondayXs_Featured_Odds_Boost,
  2: TXTWCL_TuesdayXs_Featured_Odds_Boost,
  3: TXTWCL_WednesdayXs_Featured_Odds_Boos_ELL,
  4: TXTWCL_ThursdayXs_Featured_Odds_Boost,
  5: TXTWCL_FridayXs_Featured_Odds_Boost,
  6: TXTWCL_SaturdayXs_Featured_Odds_Boost
};

const rabTranslationMap: Record<Day, TranslationTag> = {
  0: TXTWCL_SundayXs_Featured_Request_A_Be_ELL,
  1: TXTWCL_MondayXs_Featured_Request_A_Be_ELL,
  2: TXTWCL_TuesdayXs_Featured_Request_A_B_ELL,
  3: TXTWCL_WednesdayXs_Featured_Request_A_ELL,
  4: TXTWCL_ThursdayXs_Featured_Request_A__ELL,
  5: TXTWCL_FridayXs_Featured_Request_A_Be_ELL,
  6: TXTWCL_SaturdayXs_Featured_Request_A__ELL
};

const translationMap = (marketType: MarketType) =>
  marketType === 'DAILY_POWER_PRICES' ? oddsBoostTranslationMap : rabTranslationMap;

const COMBINABLE_LIST_TEMPLATE_IDS: (MarketLevelMarketTemplateId | EventLevelMarketTemplateId)[] = [
  MarketLevelMarketTemplateId.LIST,
  MarketLevelMarketTemplateId.ODDS_BOOST
];

const isCombinableListTemplateId = (
  templateId: MarketLevelMarketTemplateId | EventLevelMarketTemplateId
): templateId is MarketLevelMarketTemplateId => COMBINABLE_LIST_TEMPLATE_IDS.includes(templateId);

const TemplateGroup: React.FC<Props> = ({ isGroupedByDate, isOpen, templates }) => {
  const [firstTemplate] = templates;
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) =>
    widgetSelect.market(state, firstTemplate.marketId)
  );

  if (!market) return null;

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        openInitially={isOpen}
        title={
          <Title>
            {isGroupedByDate && isTranslatableMarketType(market.marketType) ? (
              <Translation
                {...translationMap(market.marketType)[new Date(market.marketTime).getDay() as Day]}
              />
            ) : (
              market.marketName
            )}
          </Title>
        }
      >
        {templates.length > 0 ? (
          isCombinableListTemplateId(firstTemplate.templateId) ? (
            <CombinedList
              firstMarket={market}
              templateId={firstTemplate.templateId}
              marketIds={templates.map((template) => template.marketId)}
            />
          ) : null
        ) : (
          <MarketTemplate templateId={firstTemplate.templateId} marketId={market.marketId} />
        )}
      </ExpandablePanel>
    </div>
  );
};

export default TemplateGroup;
